import { Suspense } from 'react';
import { Navigate, Outlet, type RouteObject, lazyRouteComponent } from 'src/lib/routing';
import { FIGMA_AUTH_CALLBACK_PATH } from 'src/hooks/useFigmaAuth';

import { ErrorBoundaryChatRoute } from 'components/ErrorBoundaryChatRoute/ErrorBoundaryChatRoute';
import { LoadingSuspense } from 'components/LoadingSuspense';
import { NotFound } from 'components/NotFound/NotFound';
import { SessionsRedirect } from 'components/SessionsRedirect/SessionsRedirect';
import CreditsOrderAction from 'components/dashboard/creditsOrderAction';
import JoinNUFPAction from 'components/dashboard/joinNUFPAction';
import RequestJoinAction from 'components/dashboard/requestJoinAction';
import { AIQueryAnalyticsContextProvider } from 'containers/AIQuery/AIQueryAnalytics';
import { ChatFollowupsContextProvider } from 'containers/AIQuery/containers/AIQueryChatContainer/components/ChatFollowups/ChatFollowupsContext';
import { RequireAuth } from 'containers/Auth/RequireAuth';
import { TeamContextResolver } from 'containers/Auth/TeamContextResolver';
import { discoverRoutes } from 'containers/Discover/discover-routes';
import { explorerRoutes } from 'containers/Explorer/explorer-routes';
import { focusGroupRoutes } from 'containers/FocusGroups/focus-group-routes';
import { integrationAuthRoutes } from 'containers/IntegrationAuth/integration-auth-routes';
import { settingsRoutes } from 'containers/Settings/settings-routes';
import { studiesRoutes } from 'containers/Studies/studies-routes';
import { RedirectToTeams } from 'containers/Teams/RedirectToTeams';
import { teamRoutes } from 'containers/Teams/teams-routes';
import { UploadRedirect } from 'containers/Uploads/UploadRedirect';

import App from './components/app/view';
import { LoginContainer } from './components/login/view';
import LogoutContainer from './components/logout/view';
import { SignupContainer } from './components/signup/view';
import { askablePlusRoutes } from './components/askablePlus/askable-plus-routes';
import { bookingRoutes } from './containers/Booking/booking-routes';
import { researcherRoutes } from './containers/ResearcherRegister/researcher-routes';
import { streamsRoutes } from './containers/Streams/streams-routes';
import {
  BookingsRedirect,
  DefaultRedirect,
  RequireExplorer,
  RequireQueryAccess,
  RequireStreams,
  RouteProtection,
} from './route-guards';

const ParticipantRedirect = lazyRouteComponent(
  () => import('./components/ErrorBoundaryParticipantRedirect/ErrorBoundaryParticipantRedirect'),
);

const LoginSelectContainer = lazyRouteComponent(() => import('./containers/LoginSelect/LoginSelectContainer'));
const ManageAskablePlusContainer = lazyRouteComponent(() => import('./components/manageAskablePlus/askablePlus'));
const AskablePlusProjectCompletedContainer = lazyRouteComponent(
  () => import('./components/manageAskablePlus/components/projectCompletedScreen'),
);
const CreateBooking = lazyRouteComponent(() => import('./components/createBooking/createBooking'));
const AIQueryContainer = lazyRouteComponent(() => import('containers/AIQuery/AIQueryContainer'), 'AIQueryContainer');
const AIQueryChatContainer = lazyRouteComponent(
  () => import('containers/AIQuery/containers/AIQueryChatContainer/AIQueryChatContainer'),
  'AIQueryChatContainer',
);
const AIQueryLandingContainer = lazyRouteComponent(
  () => import('containers/AIQuery/containers/AIQueryLandingContainer/AIQueryLandingContainer'),
  'AIQueryLandingContainer',
);
const AIQuerySharedChatContainer = lazyRouteComponent(
  () => import('containers/AIQuery/containers/AIQuerySharedChatContainer/AIQuerySharedChatContainer'),
  'AIQuerySharedChatContainer',
);
const CompleteProfileContainer = lazyRouteComponent(
  () => import('containers/CompleteProfile/CompleteProfileContainer'),
  'CompleteProfileContainer',
);
const Docs = lazyRouteComponent(() => import('containers/Docs/DocsContainer'), 'Docs');
const FigmaAuthCallbackContainer = lazyRouteComponent(
  () => import('containers/FigmaAuth/FigmaAuthCallbackContainer'),
  'FigmaAuthCallbackContainer',
);
const FilesContainer = lazyRouteComponent(() => import('containers/Files/FilesContainer'), 'FilesContainer');
const Highlight = lazyRouteComponent(() => import('containers/Highlights/HighlightContainer'), 'Highlight');
const ResetDemosContainer = lazyRouteComponent(
  () => import('containers/ResetDemos/ResetDemosContainer'),
  'ResetDemosContainer',
);
const BookingsContainer = lazyRouteComponent(
  () => import('containers/Root/Bookings/BookingsContainer'),
  'BookingsContainer',
);
const ProjectsContainer = lazyRouteComponent(
  () => import('containers/Root/Projects/ProjectsContainer'),
  'ProjectsContainer',
);

const ContentLoading = () => {
  return <div className="p-4">Loading...</div>;
};

/**
 * The RR-style shim route config, grafted onto the file-based root as
 * runtime-only children in `src/router.tsx` — invisible to TanStack's typed
 * `Link`. Being migrated route-by-route into `src/routes/`; this config and
 * the shim die when the last entry moves. New routes go in `src/routes/`.
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <RequireAuth>
        <TeamContextResolver>
          <App />
        </TeamContextResolver>
      </RequireAuth>
    ),
    children: [
      {
        path: 'share/:sharedChatId',
        element: (
          <AIQueryAnalyticsContextProvider>
            <AIQuerySharedChatContainer />
          </AIQueryAnalyticsContextProvider>
        ),
      },
      {
        path: '',
        element: (
          <RouteProtection>
            <Outlet />
          </RouteProtection>
        ),
        children: [
          { index: true, element: <DefaultRedirect /> },
          {
            path: 'chat',
            element: (
              <RequireQueryAccess>
                <ErrorBoundaryChatRoute showChatOption>
                  <AIQueryAnalyticsContextProvider>
                    <AIQueryContainer />
                  </AIQueryAnalyticsContextProvider>
                </ErrorBoundaryChatRoute>
              </RequireQueryAccess>
            ),
            children: [
              { index: true, element: <AIQueryLandingContainer /> },
              {
                path: ':chatId',
                element: (
                  <ChatFollowupsContextProvider>
                    <AIQueryChatContainer />
                  </ChatFollowupsContextProvider>
                ),
              },
            ],
          },
          {
            path: 'docs',
            element: (
              <RequireQueryAccess>
                <FilesContainer />
              </RequireQueryAccess>
            ),
          },
          {
            path: '/docs/:docId',
            element: (
              <RequireQueryAccess>
                <Docs />
              </RequireQueryAccess>
            ),
          },
          {
            path: '/highlights/:evidenceId',
            element: (
              <RequireQueryAccess>
                <Highlight />
              </RequireQueryAccess>
            ),
          },
          {
            path: '/',
            element: <Outlet />,
            children: [
              { path: 'studies', element: <BookingsContainer /> },
              { path: 'askable-plus', element: <ProjectsContainer /> },
              { path: 'dashboard', element: <DefaultRedirect /> },
            ],
          },
          { path: 'join-nufp', element: <JoinNUFPAction /> },
          { path: 'request_join', element: <RequestJoinAction /> },
          { path: 'credits/order', element: <CreditsOrderAction /> },
          ...bookingRoutes,
          ...settingsRoutes,
          { path: 'manage-booking/:bookingID/:screen?', element: <BookingsRedirect /> },
          { path: 'bookings/:bookingID/applicants', element: <BookingsRedirect /> },
          { path: 'bookings/:bookingID/sessions', element: <BookingsRedirect /> },
          {
            path: 'manage-askable-plus-project',
            element: <Outlet />,
            children: [
              { path: 'project-completed', element: <AskablePlusProjectCompletedContainer /> },
              {
                path: ':projectID',
                element: (
                  <Suspense fallback={<ContentLoading />}>
                    <ManageAskablePlusContainer />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'booking-setup',
            children: [
              { path: ':screen', element: <CreateBooking /> },
              { path: ':bookingID/:screen/:subScreen', element: <CreateBooking /> },
            ],
          },
          ...askablePlusRoutes,
          ...studiesRoutes,
          ...focusGroupRoutes,
          ...discoverRoutes,
          {
            path: '/',
            element: (
              <RequireExplorer>
                <Outlet />
              </RequireExplorer>
            ),
            children: explorerRoutes,
          },
          ...teamRoutes,
          ...researcherRoutes,
          {
            path: 'streams',
            element: (
              <RequireStreams>
                <Suspense fallback={<ContentLoading />}>
                  <Outlet />
                </Suspense>
              </RequireStreams>
            ),
            children: streamsRoutes,
          },
        ],
      },
    ],
  },
  {
    path: 'complete-profile',
    element: (
      <RequireAuth>
        <CompleteProfileContainer />
      </RequireAuth>
    ),
  },
  {
    path: FIGMA_AUTH_CALLBACK_PATH,
    element: (
      <RequireAuth>
        <FigmaAuthCallbackContainer />
      </RequireAuth>
    ),
  },
  ...integrationAuthRoutes,
  { path: 'join-team/:requestId', element: <RedirectToTeams /> },
  { path: 'shared-invite/:teamId', element: <RedirectToTeams /> },
  { path: 'signin-another-team', element: <RedirectToTeams /> },
  { path: 'join-new-team', element: <RedirectToTeams /> },
  { path: '/signup', element: <SignupContainer /> },
  { path: '/login', element: <LoginContainer /> },
  { path: '/logout', element: <LogoutContainer /> },
  { path: '/sso-login', element: <Navigate to="/login" replace /> },
  { path: '/participant-redirect', element: <ParticipantRedirect /> },
  {
    path: '/askable-sessions/*',
    element: (
      <RequireAuth>
        <SessionsRedirect />
      </RequireAuth>
    ),
  },
  {
    path: '/reset-demos',
    element: (
      <RequireAuth>
        <ResetDemosContainer />
      </RequireAuth>
    ),
  },
  {
    // Landing page for export file links. RequireAuth reads `?user_type=client` to send a
    // signed-out visitor to the correct login before a fresh signed URL is made.
    path: '/uploads/:taskResultId',
    element: (
      <RequireAuth>
        <UploadRedirect />
      </RequireAuth>
    ),
  },
  {
    path: 'login-select',
    element: (
      <LoadingSuspense>
        <LoginSelectContainer />
      </LoadingSuspense>
    ),
  },
  { path: '*', element: <NotFound /> },
];
